import { useApi } from '../../composables/useApi';
import { useStore } from 'vuex';

/**
 * Single ZDHC service for the Gateway 2.0 flow: every call goes to our
 * Lumen backend (same auth as the other internal services), which proxies
 * the ZDHC SP API server-side. The browser never talks to ZDHC anymore.
 *
 * Replaces ZdhcServiceV2/V4/V5 + zdhcClient/useZdhcApiClient (kept in the
 * tree but no longer imported by the live flow — removal happens in step 2).
 *
 * Error contract: methods return the response payload on success, or an
 * object { error, status, message, errors } for ZDHC domain errors
 * (zdhc_* codes from the backend Handler). Callers decide the UX.
 */

export const LINK_STATUS = {
    NOT_LINKED: 'not_linked',
    LINKED_NO_SUBSCRIPTION: 'linked_no_subscription',
    READY: 'ready',
};

export const INVENTORY_STATUS = {
    DRAFT: 'draft',
    SUBMITTED: 'submitted',
    PUBLISHED: 'published',
    DECLINED: 'declined',
};

/**
 * Maps a catalogue item of the new SP API to the shape the existing match
 * UI consumes ({id, name, guid, formulator_name, fullProduct}), so modals
 * and tables keep working without a restructuring (plan §4.4). `id` is the
 * product_reference_id: the local zdhc_products cache uses it as key for
 * products discovered through the new search (plan §4.3.3).
 */
export const toMatchShape = (item) => ({
    id: item.product_reference_id,
    product_reference_id: item.product_reference_id,
    name: item.product_name,
    guid: null,
    formulator_name: item.formulator_name,
    fullProduct: {
        productID: item.product_reference_id,
        productName: item.product_name,
        formulatorName: item.formulator_name,
        formulator_reference_id: item.formulator_reference_id ?? null,
        registered: 'ZDHC Gateway',
        conformance_level_name: item.conformance_level_name ?? null,
        ctz_level_name: item.ctz_level_name ?? null,
        local_language_names: item.local_language_names ?? [],
        zdhcPID: item.zdhc_pid ?? null,
    },
});

const ZdhcService = () =>
{
    /* Data */
    const ApiClient = useApi();
    const store = useStore();

    /* Helpers */
    const normalizeError = (e) => ({
        error: e.response?.data?.error ?? 'zdhc_unreachable',
        status: e.response?.status ?? null,
        message: e.response?.data?.message ?? null,
        errors: e.response?.data?.errors ?? null,
    });

    const call = async (fn) =>
    {
        store.commit('errors/CLEAR_STATE');
        try
        {
            const res = await fn();
            return res?.data ?? null;
        }
        catch (e)
        {
            if (e.response) return normalizeError(e);
            throw e;
        }
    };

    /* Onboarding gate */
    const linkStatus = () => call(() => ApiClient.get('zdhc/link-status'));

    /* Subscription (scoped server-side on the logged-in user's supplier) */
    const showSubscription = () => call(() => ApiClient.get('zdhc/subscription'));
    const createSubscription = () => call(() => ApiClient.post('zdhc/subscription'));

    /* Product catalogue */
    const searchProducts = ({ search = null, formulator_name = null, conformance_level = null, page = 1, per_page = 50 } = {}) =>
    {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (formulator_name) params.append('formulator_name', formulator_name);
        if (conformance_level) params.append('conformance_level', conformance_level);
        params.append('page', page);
        params.append('per_page', per_page);

        return call(() => ApiClient.get(`zdhc/products?${params.toString()}`));
    };
    /* La config e' opzionale perche' i chiamanti hanno esigenze diverse: una
       scheda aperta a mano puo' aspettare, un export che chiama il catalogo una
       volta per riga no. Senza un tetto la richiesta resta appesa per sempre —
       l'istanza axios non imposta `timeout`, quindi il default e' `0`. */
    const showProduct = (productReferenceId, config = {}) =>
        call(() => ApiClient.get(`zdhc/products/${encodeURIComponent(productReferenceId)}`, config));

    /* Risoluzione dello stato di match di un periodo. Non e' una proxy verso il
       catalogo: e' il risolutore locale, chiesto dalla singola riga della
       tabella prodotti perche' nessuno debba aspettare lo sweep schedulato.

       Best-effort: se fallisce, la riga mostra lo stato che ha gia'. Senza
       `skipErrorRedirect` un 404 — la rotta non ancora deployata — porterebbe
       l'utente alla pagina "non trovato" invece che alla dashboard.

       Il timeout non e' una precauzione teorica: senza, una richiesta appesa
       lascerebbe la riga a girare per sempre. */
    const resolveMatch = (incheckReferenceId) =>
        call(() => ApiClient.post(
            `zdhc/incheck-references/${encodeURIComponent(incheckReferenceId)}/resolve`,
            {},
            { skipErrorRedirect: true, timeout: 60000 }
        ));

    /* La scelta dell'utente fra i candidati. Se sia un match esatto o un manual
       match lo decide il backend, con lo stesso criterio della risoluzione
       automatica: qui si manda solo quale prodotto e' stato scelto.

       Nessun `skipErrorRedirect`: a differenza della risoluzione questa non e'
       una chiamata di sfondo, e i suoi errori — candidato sparito, scelta gia'
       passata, periodo gia' dichiarato — sono cose che chi ha appena cliccato
       deve vedere. */
    const associateMatch = (incheckReferenceId, productReferenceId) =>
        call(() => ApiClient.post(
            `zdhc/incheck-references/${encodeURIComponent(incheckReferenceId)}/associate`,
            { product_reference_id: productReferenceId },
            { skipErrorRedirect: true, timeout: 60000 }
        ));

    /* Il prodotto lo ha cercato l'utente, non gliel'ha proposto la ricerca
       automatica: vale dove non c'e' niente di associato. Endpoint distinto da
       `associateMatch` perche' autorizza cose diverse — quello accetta solo un
       candidato proposto, e allentarlo avrebbe riaperto la possibilita' di
       sostituire un match concluso con una richiesta vecchia. */
    const associateSearchedMatch = (incheckReferenceId, productReferenceId) =>
        call(() => ApiClient.post(
            `zdhc/incheck-references/${encodeURIComponent(incheckReferenceId)}/associate-searched`,
            { product_reference_id: productReferenceId },
            { skipErrorRedirect: true, timeout: 60000 }
        ));

    /* La verifica pre-incheck di un periodo, un lotto per richiesta. I prodotti
       non si mandano: il backend li risolve dall'utente loggato. Chi chiama
       guida il ciclo — `runPeriodRefresh` — e rimanda il cursore com'e'
       arrivato, senza ricostruirlo.

       `cursor` a `null` e' la prima richiesta della passata, e si manda proprio
       cosi': il backend distingue sul **valore**, e una stringa vuota la
       rifiuta con 422 perche' e' un cursore che il client crede di avere.

       Come per `resolveMatch`: `skipErrorRedirect` perche' un 404 — la rotta
       non ancora deployata — porterebbe l'utente alla pagina "non trovato"
       invece di lasciare la modale mostrare il proprio errore. Il timeout
       perche' l'istanza axios non ne ha uno: il lotto ha un budget di 20s lato
       server, quindi una richiesta che supera il minuto e' appesa, non lenta. */
    const refreshPeriod = (period, cursor = null) =>
        call(() => ApiClient.post(
            `zdhc/periods/${encodeURIComponent(period)}/refresh`,
            { cursor },
            { skipErrorRedirect: true, timeout: 60000 }
        ));

    /* Inventories (draft → lines → submit, asynchronous report lifecycle) */
    const indexInventories = ({ period = null, status = null, page = 1, per_page = 50 } = {}) =>
    {
        const params = new URLSearchParams();
        if (period) params.append('period', period);
        if (status) params.append('status', status);
        params.append('page', page);
        params.append('per_page', per_page);

        return call(() => ApiClient.get(`zdhc/inventories?${params.toString()}`));
    };
    const storeInventory = (payload) => call(() => ApiClient.post('zdhc/inventories', payload));
    const submitInventory = (inventoryReferenceId) =>
        call(() => ApiClient.post(`zdhc/inventories/${encodeURIComponent(inventoryReferenceId)}/submit`));
    const deleteInventory = (inventoryReferenceId) =>
        call(() => ApiClient.delete(`zdhc/inventories/${encodeURIComponent(inventoryReferenceId)}`));
    const inventoryProducts = (inventoryReferenceId) =>
        call(() => ApiClient.get(`zdhc/inventories/${encodeURIComponent(inventoryReferenceId)}/products`));

    return {
        linkStatus,
        showSubscription,
        createSubscription,
        searchProducts,
        showProduct,
        resolveMatch,
        associateMatch,
        associateSearchedMatch,
        refreshPeriod,
        indexInventories,
        storeInventory,
        submitInventory,
        deleteInventory,
        inventoryProducts,
    };
};

export default ZdhcService;
